import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useUILocale } from '../hooks/useUILocale';
import { useHreflangHead } from '../hooks/useHreflangHead';
import { withLocale } from '../locale';

const TermsPage: React.FC = () => {
  const uiLocale = useUILocale();
  useHreflangHead(uiLocale, '/terms');
  return (
    <Layout title="Terms of Service">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="bg-primary-600 px-6 py-8 text-white">
            <h1 className="text-2xl font-bold">Terms of Service</h1>
            <p className="text-primary-100 mt-1">Last updated: 25 April 2026</p>
          </div>

          <div className="p-6 space-y-6">
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">1. Acceptance of Terms</h2>
              <p className="text-gray-600 text-sm">
                By accessing or using StoryChain ("the App"), you agree to be bound by these Terms of Service.
                If you do not agree to these terms, please do not use the App.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">2. Description of Service</h2>
              <p className="text-gray-600 text-sm">
                StoryChain is a collaborative storytelling platform where users create stories together,
                one sentence at a time. The App uses AI to generate coherent final stories from user contributions.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">3. User Accounts</h2>
              <ul className="text-gray-600 text-sm space-y-1">
                <li className="flex gap-2"><span>•</span><span>
                  You can sign in with email + one-time code, with Sign in with Apple, or with Sign in with Google. Whichever method you use, you must provide accurate identifiers and keep them current.
                </span></li>
                <li className="flex gap-2"><span>•</span><span>You are responsible for maintaining the security of your account and the device you stay signed in on.</span></li>
                <li className="flex gap-2"><span>•</span><span>You may delete your account at any time. Deletion permanently removes your stories, contributions, comics, and balances.</span></li>
                <li className="flex gap-2"><span>•</span><span>
                  An account is not required to <em>join</em> a story by code — see "Anonymous Use" below.
                </span></li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">4. User Content</h2>
              <p className="text-gray-600 text-sm mb-2">
                You retain ownership of the content you create. By submitting content, you grant StoryChain
                a license to use, display, and process your content for the purpose of providing the service —
                including sending it to third-party AI providers for moderation, narrative generation, and
                cover-image generation.
              </p>
              <p className="text-gray-600 text-sm mb-2">
                Each finished story has a visibility setting (Public or Unlisted) that any participant can
                change at any time. See the <Link to={withLocale(uiLocale, '/privacy')} className="text-primary-600 hover:underline">Privacy Policy</Link> for what each setting means.
              </p>
              <p className="text-gray-600 text-sm mb-2">
                All user-submitted content is automatically reviewed by AI moderation systems.
                Content that violates our guidelines will be rejected.
              </p>
              <p className="text-gray-600 text-sm">You agree not to submit content that:</p>
              <ul className="text-gray-600 text-sm space-y-1 mt-1">
                <li className="flex gap-2"><span>•</span><span>Is illegal, harmful, or offensive</span></li>
                <li className="flex gap-2"><span>•</span><span>Infringes on others' intellectual property rights</span></li>
                <li className="flex gap-2"><span>•</span><span>Contains spam or malicious content</span></li>
                <li className="flex gap-2"><span>•</span><span>Harasses or threatens other users</span></li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">5. Coins, Pricing &amp; Refunds</h2>
              <p className="text-gray-600 text-sm mb-2">
                Some actions in StoryChain consume in-app coins — most notably creating a new story (which
                pays for the AI-generated final narrative and, if applicable, the cover image). The current
                cost of each action is shown in the app before you confirm.
              </p>
              <p className="text-gray-600 text-sm mb-2">
                Every account receives a daily free coin allowance that refills at 00:00 UTC. Free coins
                do not roll over. We may adjust the allowance and the price of individual actions over
                time as our AI costs change; any change applies only to actions taken after the change.
              </p>
              <p className="text-gray-600 text-sm">
                If an action you paid for cannot be completed because of a service or AI failure on our
                side (for example, if the model fails to generate a story), the coins for that specific
                action are automatically refunded to your balance. Coins spent on actions that did
                complete are non-refundable.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">6. Anonymous Use</h2>
              <p className="text-gray-600 text-sm">
                You can join an existing story by code without creating an account. In that case the only
                personal data we associate with your contributions is the display name you typed when
                joining, paired with an anonymous device identifier so you can keep writing on the same
                device. Anonymous participants cannot create new stories or change story visibility.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">7. AI-Generated Content</h2>
              <p className="text-gray-600 text-sm">
                Final stories and cover images are generated using third-party AI services (such as
                OpenAI, Anthropic, and xAI) based on user contributions. We do not guarantee the
                accuracy, quality, or appropriateness of AI-generated content. The AI may modify or
                expand upon user submissions to create coherent narratives.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">8. Intellectual Property</h2>
              <p className="text-gray-600 text-sm">
                The StoryChain app, logo, and service are owned by us. User-generated stories are
                jointly owned by all contributors to that story.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">9. Disclaimer of Warranties</h2>
              <p className="text-gray-600 text-sm">
                The App is provided "as is" without warranties of any kind. We do not guarantee
                uninterrupted or error-free service.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">10. Limitation of Liability</h2>
              <p className="text-gray-600 text-sm">
                To the maximum extent permitted by law, StoryChain shall not be liable for any
                indirect, incidental, or consequential damages arising from your use of the App.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">11. Termination</h2>
              <p className="text-gray-600 text-sm">
                We reserve the right to suspend or terminate your access to the App at any time
                for violations of these terms or for any other reason at our discretion.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">12. Changes to Terms</h2>
              <p className="text-gray-600 text-sm">
                We may update these terms from time to time. Continued use of the App after changes
                constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">13. Contact</h2>
              <p className="text-gray-600 text-sm">
                For questions about these Terms, contact us at:{' '}
                <a href="mailto:support@storychain.fun" className="text-primary-600 hover:underline">support@storychain.fun</a>
              </p>
            </section>
          </div>
        </div>

        <p className="text-center text-sm text-gray-400 mt-6">
          <Link to={withLocale(uiLocale, '/privacy')} className="hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </Layout>
  );
};

export default TermsPage;
